/**
 * Route-level code splitting (docs/adr/0042-fast-web.md): every page is its own chunk, loaded when
 * its route is first opened — or a moment earlier, when the pointer rests on a link to it
 * (`preload`), so the navigation itself does not wait for the network.
 *
 * A tab that was opened before a release asks for chunk files of the OLD release. The service
 * worker keeps those (public/sw.js precaches every chunk of the release and keeps the previous
 * release's too), so this normally just works; when a chunk is really gone (no service worker,
 * cleared storage), `importWithReload` reloads the page ONCE — the address already names the
 * destination, and the fresh shell loads the new release's chunks.
 */
import { createElement, lazy, useState, type ComponentType } from 'react';

const RELOAD_KEY = 'xc.chunk-reload-at';
/** One automatic reload per this many ms: a chunk that is still missing after it is a real error (the error boundary shows it). */
const RELOAD_EVERY_MS = 60_000;

function readMark(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY) ?? 0) || 0;
  } catch {
    return Date.now(); // no storage: never loop on reloads
  }
}

/** Whether a failed chunk load may reload the page now (and remembers that it did). Exported for tests. */
export function mayReloadForChunk(now = Date.now()): boolean {
  if (now - readMark() < RELOAD_EVERY_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  return true;
}

/** No network at all: a chunk that fails then is not a stale release (reloading would only show the offline shell). */
export const isOffline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * A chunk of the app that could not be fetched — offline, a release that moved on, a transient 5xx on
 * /assets/ — as browsers (Chrome, Firefox, Safari) and Vite word it; `StaleReleaseError` is the API
 * client's own name for it. Anything else thrown while rendering is a bug, not a download.
 */
export function isChunkLoadError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  return e.name === 'StaleReleaseError' || /dynamically imported module|Importing a module script failed|Loading (?:CSS )?chunk|Unable to preload/i.test(e.message);
}

export function importWithReload<T>(load: () => Promise<T>, reload: () => void = () => window.location.reload()): Promise<T> {
  return load().catch((error: unknown) => {
    // Offline is not a stale release: reloading would only replace the page with the offline shell.
    if (!isOffline() && mayReloadForChunk()) {
      reload();
      return new Promise<T>(() => undefined); // the page is going away; keep the fallback on screen
    }
    throw error;
  });
}

export type LazyPage<P> = ComponentType<P> & { preload: () => void };

/**
 * `lazyPage(() => import('./pages/FilesPage.js'), 'FilesPage')` — a lazy component for one named export.
 *
 * A page whose code is already here (preloaded: App.tsx starts the chunk of the address in the bar
 * while the session is still being asked for) renders at once, without a Suspense round: React
 * keeps a fallback it has just shown for at least 300 ms, so even a chunk that arrives a moment
 * after the fallback would otherwise hold a ready page back. Which of the two a mounted page uses
 * is decided once, so it never remounts (and never loses its state) when the chunk arrives.
 *
 * A chunk that could not be fetched (offline, a stale release that did not reload) is tried again by
 * the NEXT mount — a later navigation, a dialog opened once more, "ลองอีกครั้ง": React keeps a rejected
 * lazy's error for good, so the lazy wrapper is made anew after every failure.
 */
export function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K): LazyPage<M[K] extends ComponentType<infer P> ? P : never> {
  type P = M[K] extends ComponentType<infer Q> ? Q : never;
  let promise: Promise<{ default: ComponentType<P> }> | null = null;
  let loaded: ComponentType<P> | null = null;
  const run = () =>
    (promise ??= importWithReload(load).then(
      (m) => ({ default: (loaded = m[name] as unknown as ComponentType<P>) }),
      (e: unknown) => {
        promise = null; // a later navigation may try again (e.g. back online)
        Lazy = lazy(run); // the wrapper that saw the failure would keep showing it
        throw e;
      },
    ));
  let Lazy = lazy(run);
  function Page(props: P) {
    const [Component] = useState<ComponentType<P>>(() => loaded ?? Lazy);
    return createElement(Component as ComponentType<object>, props as object);
  }
  Page.displayName = `Page(${String(name)})`;
  return Object.assign(Page, { preload: () => void run().catch(() => undefined) }) as unknown as LazyPage<P>;
}

/**
 * Run `fn` once the browser is idle (at the latest after `timeout` ms) — to warm a chunk the page is
 * likely to need soon (the file preview, a dialog) without putting it on the first screen's path.
 * Returns a cancel function for an effect's cleanup.
 */
export function whenIdle(fn: () => void, timeout = 2500): () => void {
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
  if (w.requestIdleCallback && w.cancelIdleCallback) {
    const id = w.requestIdleCallback(fn, { timeout });
    return () => w.cancelIdleCallback!(id);
  }
  const t = setTimeout(fn, Math.min(timeout, 1200));
  return () => clearTimeout(t);
}
